import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { money } from "../../lib/api";
import { useSupplier } from "../../context/SupplierContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { removeCartItemsForSeller } from "../../lib/cart";
import {
  FileText, Plus, Trash2, ArrowRight, Download, Send, Building2,
  Search, Calendar, ShoppingBag, Sparkles, Check, X, Lock, ShoppingCart,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// BuyerPOGenerator — a full Purchase Order builder
// Buyer fills out PO header + line items → preview → submit to seller as a pending order.
// Seller will then confirm and issue the matching invoice (existing flow).
// ─────────────────────────────────────────────────────────────────────────────

const today = () => new Date().toISOString().slice(0, 10);

export default function BuyerPOGenerator() {
  const nav = useNavigate();
  const location = useLocation();
  // When buyer arrives here via Cart → "Continue to generate PO", the
  // route state carries the cart items + seller_id + terms so we can
  // pre-fill the PO without an extra step.
  const cartHandoff = location.state && location.state.fromCart ? location.state : null;
  const { active: activeSupplier } = useSupplier();
  const [products, setProducts] = useState([]);
  const [user, setUser] = useState(null);
  const [connections, setConnections] = useState([]);  // buyer's connected suppliers (with or without catalog)
  // When the buyer is in a specific supplier's workspace, lock the seller
  // dropdown to that supplier. The user can still click "Switch supplier"
  // to override (which clears the lock for the current PO).
  const [sellerLocked, setSellerLocked] = useState(true);

  // PO header
  const [seller, setSeller] = useState(cartHandoff?.seller_id || "");
  const [poNumber, setPoNumber] = useState(`PO-${Math.floor(100000 + Math.random() * 900000)}`);
  const [poDate, setPoDate] = useState(today());
  const [reqDate, setReqDate] = useState(new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10));
  const [terms, setTerms] = useState(cartHandoff?.terms || "net30");
  const [shipTo, setShipTo] = useState("");
  const [notes, setNotes] = useState("");

  // Line items — pre-filled from cart handoff if present, else one empty row
  const [lines, setLines] = useState(() => {
    if (cartHandoff?.items?.length) {
      return cartHandoff.items.map((it, i) => ({
        id: i + 1,
        sku: it.sku || "",
        name: it.name,
        qty: it.qty,
        price: it.price,
        moq: it.moq,
      }));
    }
    return [{ id: 1, sku: "", name: "", qty: 1, price: 0 }];
  });
  const [picker, setPicker] = useState({ open: false, lineId: null, search: "" });

  // Preview
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    api.get("/products/all").then(r => setProducts(r.data));
    api.get("/auth/me").then(r => setUser(r.data)).catch(() => {});
    // Connected suppliers — buyer should be able to PO any of them, even
    // if the supplier has no catalog (free-form line items still work).
    api.get("/buyer/connections").then(r => setConnections(r.data || [])).catch(() => {});
  }, []);

  // Auto-pre-select the active supplier from the workspace switcher.
  // Only sets it once (when seller is empty) so the user can still change
  // sellers manually after unlocking.
  useEffect(() => {
    if (activeSupplier?.tenant_owner_user_id && !seller) {
      setSeller(activeSupplier.tenant_owner_user_id);
    }
  }, [activeSupplier, seller]);

  const sellers = useMemo(() => {
    // Union of (a) sellers we have catalog products from and (b) all
    // connected suppliers. Connections fill the gap when a supplier has
    // uninstalled their catalog app — buyer can still send them a PO.
    // Connections take priority for the display name so we show the
    // tenant brand (e.g. "GlowCo Beauty") not the user's personal name.
    const map = {};
    connections.forEach(c => {
      const sid = c.tenant_owner_user_id || c.tenant_id;
      if (sid) map[sid] = { id: sid, name: c.tenant_name || c.seller_name || "Supplier" };
    });
    products.forEach(p => {
      if (!map[p.seller_id]) map[p.seller_id] = { id: p.seller_id, name: p.seller_name };
    });
    return Object.values(map);
  }, [products, connections]);

  const sellerProducts = useMemo(
    () => products.filter(p => !seller || p.seller_id === seller),
    [products, seller],
  );

  const subtotal = lines.reduce((s, l) => s + (parseFloat(l.qty) || 0) * (parseFloat(l.price) || 0), 0);
  const lineCount = lines.filter(l => l.name).length;

  const updateLine = (id, k, v) => setLines(prev => prev.map(l => l.id === id ? { ...l, [k]: v } : l));
  const addLine = () => setLines(prev => [...prev, { id: Date.now(), sku: "", name: "", qty: 1, price: 0 }]);
  const dropLine = (id) => setLines(prev => prev.length > 1 ? prev.filter(l => l.id !== id) : prev);

  const openPicker = (lineId) => setPicker({ open: true, lineId, search: "" });
  const pickProduct = (p) => {
    if (!seller) setSeller(p.seller_id); // lock to picked seller
    if (seller && p.seller_id !== seller) return toast.error("PO is locked to one seller — clear lines or start a new PO");
    setLines(prev => prev.map(l => l.id === picker.lineId ? { ...l, sku: p.sku, name: p.name, price: p.price, moq: p.moq } : l));
    setPicker({ open: false, lineId: null, search: "" });
  };

  const validate = () => {
    if (!seller) return "Pick a seller";
    if (!shipTo.trim()) return "Ship-to address is required";
    if (lineCount === 0) return "Add at least one line item";
    for (const l of lines) {
      if (l.name && (!l.qty || l.qty < 1)) return `Quantity required for "${l.name}"`;
      if (l.name && (!l.price || l.price <= 0)) return `Price required for "${l.name}"`;
    }
    return null;
  };

  const downloadPDF = () => {
    // Generate a simple text-based PO file (mock — real impl would server-side render PDF)
    const lines_txt = lines.filter(l => l.name).map(l => `${l.sku || "—"}\t${l.name}\t${l.qty}\t$${l.price}\t$${(l.qty * l.price).toFixed(2)}`).join("\n");
    const sname = sellers.find(s => s.id === seller)?.name || "—";
    const txt = [
      `PURCHASE ORDER`, `=================`,
      `PO #: ${poNumber}`,
      `Date: ${poDate}`,
      `Required by: ${reqDate}`,
      `Terms: ${terms.toUpperCase()}`,
      ``,
      `BUYER:    ${user?.name || "—"} · ${user?.brand || user?.company || ""}`,
      `SELLER:   ${sname}`,
      `SHIP TO:  ${shipTo}`,
      ``,
      `SKU\tDescription\tQty\tPrice\tLine total`,
      lines_txt,
      ``,
      `SUBTOTAL: $${subtotal.toFixed(2)}`,
      ``,
      `Notes: ${notes || "—"}`,
    ].join("\n");
    const blob = new Blob([txt], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${poNumber}.txt`; a.click();
    URL.revokeObjectURL(url);
    toast.success(`${poNumber}.txt downloaded`);
  };

  const submitPO = async () => {
    const err = validate();
    if (err) return toast.error(err);
    try {
      const items = lines.filter(l => l.name).map(l => ({
        product_id: l.sku || `po-${l.name.slice(0, 8)}`,
        name: l.name,
        qty: parseInt(l.qty) || 1,
        price: parseFloat(l.price) || 0,
      }));
      await api.post("/orders", { seller_id: seller, terms, items });
      // If this PO was generated from cart, clear those cart items so
      // the buyer doesn't accidentally re-submit them.
      if (cartHandoff?.fromCart && cartHandoff.seller_id) {
        try { removeCartItemsForSeller(cartHandoff.seller_id); } catch (_e) { /* ignore */ }
      }
      toast.success(`${poNumber} submitted — waiting for seller to confirm`);
      nav("/buyer/orders#journey");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit PO");
    }
  };

  return (
    <div className="min-h-screen" data-testid="po-generator">
      {/* Sticky header */}
      <div className="border-b border-border bg-background/80 backdrop-blur sticky top-0 z-20 px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">Apps · Procurement</div>
          <h1 className="display text-2xl mt-1 tracking-tight">PO Generator</h1>
          <div className="text-xs text-foreground/55 mt-0.5">Auto-build a Purchase Order &amp; submit to the seller in one click</div>
        </div>
        <div className="flex gap-2 items-center">
          <Button variant="outline" onClick={downloadPDF} data-testid="po-download" className="rounded-full h-10 px-4">
            <Download className="w-4 h-4 mr-1.5"/>Download PO
          </Button>
          <Button variant="outline" onClick={() => setPreview(true)} data-testid="po-preview" className="rounded-full h-10 px-4">
            Preview
          </Button>
          <Button onClick={submitPO} data-testid="po-submit" className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-10 px-4">
            <Send className="w-4 h-4 mr-1.5"/>Submit to seller
          </Button>
        </div>
      </div>

      <div className="page-shell-narrow space-y-6">

        {/* Cart handoff banner — shown when the buyer arrived via Cart "Continue to PO" */}
        {cartHandoff && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 px-5 py-3.5 flex items-center gap-3 text-sm" data-testid="cart-handoff-banner">
            <span className="w-8 h-8 rounded-lg bg-emerald-600 text-white grid place-items-center shrink-0">
              <ShoppingCart className="w-4 h-4"/>
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-emerald-900">
                Loaded {cartHandoff.items.length} item{cartHandoff.items.length !== 1 ? "s" : ""} from your cart
              </div>
              <div className="text-[12px] text-emerald-800/80 mt-0.5">
                Adjust terms · shipping address · delivery date below, then submit to the seller. We&apos;ll clear these from your cart automatically.
              </div>
            </div>
            <button onClick={() => nav("/buyer/cart")} className="text-[12px] text-emerald-900/70 hover:text-emerald-900 underline-offset-2 hover:underline">
              Back to cart
            </button>
          </div>
        )}

        {/* PO header */}
        <div className="rounded-2xl bg-white border border-border p-7" data-testid="po-header">
          <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50 mb-4">PO header</div>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label>PO #</Label>
              <Input value={poNumber} onChange={e => setPoNumber(e.target.value)} className="mt-1.5 h-11 mono" data-testid="po-number"/>
            </div>
            <div>
              <Label>PO date</Label>
              <Input type="date" value={poDate} onChange={e => setPoDate(e.target.value)} className="mt-1.5 h-11"/>
            </div>
            <div>
              <Label>Required by</Label>
              <Input type="date" value={reqDate} onChange={e => setReqDate(e.target.value)} className="mt-1.5 h-11" data-testid="req-date"/>
            </div>
            <div className="md:col-span-2">
              <Label>Seller / supplier</Label>
              {activeSupplier && sellerLocked && seller === activeSupplier.tenant_owner_user_id ? (
                <div className="mt-1.5 h-11 rounded-md border border-input bg-secondary/50 px-3 flex items-center justify-between" data-testid="po-seller-locked">
                  <div className="flex items-center gap-2 min-w-0">
                    <Lock className="w-3.5 h-3.5 text-foreground/50 shrink-0"/>
                    <span className="text-sm font-medium truncate">{activeSupplier.tenant_name || "Active supplier"}</span>
                    <span className="chip bg-foreground/8 text-[10px] uppercase tracking-widest text-foreground/55 shrink-0">From workspace</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setSellerLocked(false); setSeller(""); setLines([{ id: 1, sku: "", name: "", qty: 1, price: 0 }]); }}
                    className="text-xs text-foreground/60 hover:text-foreground underline shrink-0 ml-2"
                    data-testid="po-seller-switch"
                  >
                    Switch supplier
                  </button>
                </div>
              ) : (
                <select value={seller} onChange={e => { setSeller(e.target.value); setLines([{ id: 1, sku: "", name: "", qty: 1, price: 0 }]); }}
                  data-testid="po-seller"
                  className="mt-1.5 w-full h-11 rounded-md border border-input bg-white px-3 text-sm">
                  <option value="">— Pick a seller —</option>
                  {sellers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              )}
              {seller && <div className="mt-1.5 text-[10px] text-foreground/50 inline-flex items-center gap-1"><Building2 className="w-2.5 h-2.5"/>One PO per seller — to add another supplier, submit and start a new PO.</div>}
            </div>
            <div>
              <Label>Payment terms</Label>
              <select value={terms} onChange={e => setTerms(e.target.value)} className="mt-1.5 w-full h-11 rounded-md border border-input bg-white px-3 text-sm" data-testid="po-terms">
                <option value="prepaid">Prepaid</option>
                <option value="net14">Net-14</option>
                <option value="net30">Net-30</option>
                <option value="net60">Net-60</option>
                <option value="net90">Net-90</option>
              </select>
            </div>
            <div className="md:col-span-3">
              <Label>Ship-to address</Label>
              <Input value={shipTo} onChange={e => setShipTo(e.target.value)}
                placeholder="Apex Wellness · 123 Industrial Way, Brooklyn NY 11201" data-testid="po-shipto" className="mt-1.5 h-11"/>
            </div>
          </div>
        </div>

        {/* Line items */}
        <div className="rounded-2xl bg-white border border-border p-7" data-testid="po-lines">
          <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
            <div>
              <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50">Line items</div>
              <div className="display text-lg mt-1">{lineCount} item{lineCount !== 1 ? "s" : ""} · {money(subtotal)} subtotal</div>
            </div>
            <Button variant="outline" onClick={addLine} className="rounded-full h-9 px-3" data-testid="po-add-line">
              <Plus className="w-3.5 h-3.5 mr-1.5"/>Add line
            </Button>
          </div>

          {/* Header row */}
          <div className="grid grid-cols-12 gap-2 px-1 pb-2 text-[10px] mono uppercase tracking-wider text-foreground/50">
            <div className="col-span-2">SKU</div>
            <div className="col-span-5">Description</div>
            <div className="col-span-1">Qty</div>
            <div className="col-span-2">Unit price</div>
            <div className="col-span-1 text-right">Total</div>
            <div className="col-span-1"/>
          </div>

          <div className="space-y-1.5">
            {lines.map((l) => {
              const total = (parseFloat(l.qty) || 0) * (parseFloat(l.price) || 0);
              return (
                <div key={l.id} className="grid grid-cols-12 gap-2 items-center" data-testid={`line-${l.id}`}>
                  <div className="col-span-2 flex gap-1">
                    <Input value={l.sku} onChange={e => updateLine(l.id, "sku", e.target.value)} placeholder="SKU" className="h-10 mono text-xs"/>
                    {seller && (
                      <button onClick={() => openPicker(l.id)} className="w-10 h-10 rounded-md border border-border hover:bg-secondary grid place-items-center shrink-0"
                        data-testid={`line-pick-${l.id}`}>
                        <Search className="w-3.5 h-3.5"/>
                      </button>
                    )}
                  </div>
                  <Input value={l.name} onChange={e => updateLine(l.id, "name", e.target.value)} placeholder="Item description" className="col-span-5 h-10"/>
                  <Input type="number" value={l.qty} onChange={e => updateLine(l.id, "qty", e.target.value)} className="col-span-1 h-10 text-center mono"/>
                  <Input type="number" value={l.price} onChange={e => updateLine(l.id, "price", e.target.value)} placeholder="0.00" className="col-span-2 h-10 mono"/>
                  <div className="col-span-1 text-right display text-sm">{money(total)}</div>
                  <div className="col-span-1 flex justify-end">
                    <button onClick={() => dropLine(l.id)} className="text-foreground/40 hover:text-rose-700"
                      data-testid={`line-remove-${l.id}`}>
                      <Trash2 className="w-4 h-4"/>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="border-t border-border mt-4 pt-4 flex justify-end gap-12 text-sm">
            <div className="text-right">
              <div className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50">Subtotal</div>
              <div className="display text-3xl mt-1">{money(subtotal)}</div>
              <div className="text-[10px] text-foreground/50 mt-0.5">{lineCount} line · shipping &amp; fees TBD</div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="rounded-2xl bg-white border border-border p-7">
          <Label>Notes / special instructions</Label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Pack each SKU in own carton · UCC-128 labels · pickup from dock 3 only…"
            className="mt-1.5 w-full h-24 rounded-xl border border-border p-3 text-sm" data-testid="po-notes"/>
        </div>

        {/* Bottom actions */}
        <div className="rounded-2xl border border-border p-6 bg-secondary/30 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="display text-lg">Ready to submit?</div>
            <div className="text-xs text-foreground/55 mt-0.5">Submitting creates a pending order for the seller — they'll confirm and issue the invoice.</div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setPreview(true)} className="rounded-full h-11 px-5">Preview</Button>
            <Button onClick={submitPO} data-testid="po-submit-bottom" className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-11 px-5">
              <Send className="w-4 h-4 mr-1.5"/>Submit to seller <ArrowRight className="w-4 h-4 ml-1.5"/>
            </Button>
          </div>
        </div>
      </div>

      {/* Product picker overlay */}
      {picker.open && (
        <div className="fixed inset-0 z-50 bg-foreground/40 grid place-items-center p-6" onClick={() => setPicker({ open: false, lineId: null, search: "" })}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-border flex items-center gap-2">
              <Search className="w-4 h-4 text-foreground/40"/>
              <Input value={picker.search} onChange={e => setPicker({ ...picker, search: e.target.value })}
                placeholder="Search SKU or product name…" className="h-10" autoFocus/>
              <button onClick={() => setPicker({ open: false, lineId: null, search: "" })} className="text-foreground/40 hover:text-foreground"><X className="w-4 h-4"/></button>
            </div>
            <div className="overflow-y-auto p-3 flex-1">
              {sellerProducts
                .filter(p => !picker.search || p.name.toLowerCase().includes(picker.search.toLowerCase()) || p.sku.toLowerCase().includes(picker.search.toLowerCase()))
                .map(p => (
                  <button key={p.id} onClick={() => pickProduct(p)} data-testid={`pick-${p.sku}`}
                    className="w-full text-left p-3 rounded-xl hover:bg-secondary flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-secondary overflow-hidden shrink-0">
                      {p.image ? <img src={p.image} alt={p.name} className="w-full h-full object-cover"/> : null}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{p.name}</div>
                      <div className="text-[11px] mono text-foreground/55">{p.sku} · MOQ {p.moq}</div>
                    </div>
                    <div className="display text-sm">{money(p.price)}</div>
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 z-50 bg-foreground/40 grid place-items-center p-6" onClick={() => setPreview(false)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="po-preview-modal">
            <div className="p-8 border-b border-border flex items-start justify-between">
              <div>
                <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">Purchase Order</div>
                <div className="display text-3xl tracking-tight mt-1">{poNumber}</div>
              </div>
              <button onClick={() => setPreview(false)}><X className="w-5 h-5 text-foreground/50"/></button>
            </div>
            <div className="p-8 space-y-6">
              <div className="grid grid-cols-2 gap-6 text-sm">
                <div>
                  <div className="text-[10px] mono uppercase tracking-wider text-foreground/50">Buyer</div>
                  <div className="font-medium mt-1">{user?.name || "—"}</div>
                  <div className="text-xs text-foreground/65">{user?.brand || user?.company || ""}</div>
                </div>
                <div>
                  <div className="text-[10px] mono uppercase tracking-wider text-foreground/50">Seller</div>
                  <div className="font-medium mt-1">{sellers.find(s => s.id === seller)?.name || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] mono uppercase tracking-wider text-foreground/50">Ship to</div>
                  <div className="text-xs mt-1">{shipTo || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] mono uppercase tracking-wider text-foreground/50">Dates &amp; terms</div>
                  <div className="text-xs mt-1">PO {poDate} · Required {reqDate} · {terms.toUpperCase()}</div>
                </div>
              </div>
              <table className="w-full text-sm">
                <thead className="text-[10px] mono uppercase tracking-wider text-foreground/50 border-b border-border">
                  <tr>
                    <th className="text-left py-2">SKU</th>
                    <th className="text-left py-2">Description</th>
                    <th className="text-right py-2">Qty</th>
                    <th className="text-right py-2">Unit</th>
                    <th className="text-right py-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.filter(l => l.name).map(l => (
                    <tr key={l.id} className="border-b border-border">
                      <td className="py-2 mono text-xs text-foreground/65">{l.sku || "—"}</td>
                      <td className="py-2">{l.name}</td>
                      <td className="py-2 text-right">{l.qty}</td>
                      <td className="py-2 text-right">{money(l.price)}</td>
                      <td className="py-2 text-right display">{money(l.qty * l.price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="text-right">
                <div className="text-[10px] mono uppercase tracking-wider text-foreground/50">Subtotal</div>
                <div className="display text-3xl">{money(subtotal)}</div>
              </div>
              {notes && (
                <div className="rounded-xl bg-secondary/40 p-4">
                  <div className="text-[10px] mono uppercase tracking-wider text-foreground/50">Notes</div>
                  <div className="text-sm mt-1">{notes}</div>
                </div>
              )}
              <div className="flex gap-2 justify-end pt-4 border-t border-border">
                <Button variant="outline" onClick={downloadPDF} className="rounded-full"><Download className="w-3.5 h-3.5 mr-1.5"/>Download</Button>
                <Button onClick={() => { setPreview(false); submitPO(); }} className="rounded-full bg-foreground text-background hover:bg-foreground/90">
                  <Send className="w-3.5 h-3.5 mr-1.5"/>Submit to seller
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
